import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const toNum = (v) => {
  if (v == null || v === '') return 0;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const AUTOSAVE_MS = 400;

const normalizeCategories = (rows) =>
  (Array.isArray(rows) ? rows : []).map((c) => ({
    id: c.id,
    label: c.label ?? '',
    mo_bucket: ['recurrent', 'te', 'vitrerie', 'other'].includes(c.mo_bucket) ? c.mo_bucket : 'other',
    operations: (c.operations || [])
      .map((o) => ({
        id: o.id,
        label: o.label ?? '',
        cadence_m2h: toNum(o.cadence_m2h),
        sort_order: o.sort_order,
      }))
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
  }));

const DevisOperationsTab = ({
  isActive,
  token,
  apiBase,
  theme,
  onUnauthorized,
  canConfigure,
}) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingOpKey, setSavingOpKey] = useState(null);
  const [addingCat, setAddingCat] = useState(false);
  const [newOpByCat, setNewOpByCat] = useState({});
  const [deleteCatTarget, setDeleteCatTarget] = useState(null);
  const [deleteOpTarget, setDeleteOpTarget] = useState(null);

  const hydratedRef = useRef(false);
  const catTimersRef = useRef({});
  const opTimersRef = useRef({});

  const handleApiError = useCallback(
    (error) => {
      if (error.response?.status === 401) {
        onUnauthorized?.();
        return;
      }
      const msg =
        error.response?.data?.detail ||
        error.message ||
        'Une erreur est survenue';
      toast.error(typeof msg === 'string' ? msg : 'Erreur serveur');
    },
    [onUnauthorized]
  );

  const load = useCallback(async () => {
    hydratedRef.current = false;
    setLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/operation-categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(normalizeCategories(data));
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
      hydratedRef.current = true;
    }
  }, [apiBase, token, handleApiError]);

  useEffect(() => {
    if (!isActive || !token) return;
    load();
  }, [isActive, token, load]);

  useEffect(() => {
    return () => {
      Object.values(catTimersRef.current).forEach((t) => clearTimeout(t));
      Object.values(opTimersRef.current).forEach((t) => clearTimeout(t));
      catTimersRef.current = {};
      opTimersRef.current = {};
    };
  }, []);

  const mergeCategory = useCallback((doc) => {
    const n = normalizeCategories([doc])[0];
    if (!n) return;
    setCategories((prev) => prev.map((c) => (c.id === n.id ? n : c)));
  }, []);

  const patchCategorySilent = useCallback(
    async (cat) => {
      if (!canConfigure || !token) return;
      try {
        const { data } = await axios.patch(
          `${apiBase}/operation-categories/${cat.id}`,
          {
            label: (cat.label || '').trim() || 'Catégorie',
            mo_bucket: cat.mo_bucket,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        mergeCategory(data);
      } catch (e) {
        handleApiError(e);
      }
    },
    [apiBase, token, canConfigure, handleApiError, mergeCategory]
  );

  const patchOperationSilent = useCallback(
    async (catId, op) => {
      if (!canConfigure || !token) return;
      const cad = toNum(op.cadence_m2h);
      if (!(cad > 0)) return;
      const label = (op.label || '').trim() || 'Opération';
      try {
        const { data } = await axios.patch(
          `${apiBase}/operation-categories/${catId}/operations/${op.id}`,
          { label, cadence_m2h: cad },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        mergeCategory(data);
      } catch (e) {
        handleApiError(e);
      }
    },
    [apiBase, token, canConfigure, handleApiError, mergeCategory]
  );

  const scheduleSaveCategory = useCallback(
    (cat) => {
      if (!canConfigure || !hydratedRef.current) return;
      const id = cat.id;
      if (catTimersRef.current[id]) clearTimeout(catTimersRef.current[id]);
      catTimersRef.current[id] = setTimeout(() => {
        delete catTimersRef.current[id];
        patchCategorySilent(cat);
      }, AUTOSAVE_MS);
    },
    [canConfigure, patchCategorySilent]
  );

  const scheduleSaveOperation = useCallback(
    (catId, op) => {
      if (!canConfigure || !hydratedRef.current) return;
      const key = `${catId}:${op.id}`;
      if (opTimersRef.current[key]) clearTimeout(opTimersRef.current[key]);
      opTimersRef.current[key] = setTimeout(() => {
        delete opTimersRef.current[key];
        patchOperationSilent(catId, op);
      }, AUTOSAVE_MS);
    },
    [canConfigure, patchOperationSilent]
  );

  const updateCategoryField = (catId, field, value) => {
    setCategories((prev) => {
      const next = prev.map((c) => (c.id === catId ? { ...c, [field]: value } : c));
      const cat = next.find((c) => c.id === catId);
      if (cat) scheduleSaveCategory(cat);
      return next;
    });
  };

  const updateOperationField = (catId, opId, field, raw) => {
    setCategories((prev) => {
      const next = prev.map((c) => {
        if (c.id !== catId) return c;
        return {
          ...c,
          operations: c.operations.map((o) => {
            if (o.id !== opId) return o;
            if (field === 'label') return { ...o, label: raw };
            const v =
              raw === '' || raw === null ? '' : Number(String(raw).replace(',', '.'));
            return { ...o, cadence_m2h: v };
          }),
        };
      });
      const cat = next.find((c) => c.id === catId);
      const op = cat?.operations.find((o) => o.id === opId);
      if (op) scheduleSaveOperation(catId, op);
      return next;
    });
  };

  const addCategory = async () => {
    setAddingCat(true);
    try {
      const { data } = await axios.post(
        `${apiBase}/operation-categories`,
        { label: 'Nouvelle catégorie', mo_bucket: 'other' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const n = normalizeCategories([data])[0];
      if (n) setCategories((prev) => [...prev, n]);
      toast.success('Catégorie créée.');
    } catch (e) {
      handleApiError(e);
    } finally {
      setAddingCat(false);
    }
  };

  const confirmDeleteCategory = async () => {
    if (!deleteCatTarget) return;
    try {
      await axios.delete(`${apiBase}/operation-categories/${deleteCatTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories((prev) => prev.filter((c) => c.id !== deleteCatTarget.id));
      toast.success('Catégorie supprimée.');
    } catch (e) {
      handleApiError(e);
    } finally {
      setDeleteCatTarget(null);
    }
  };

  const addOperation = async (catId) => {
    const draft = newOpByCat[catId] || { label: '', cadence: '' };
    const label = (draft.label || '').trim();
    const cad = toNum(draft.cadence);
    if (!label) {
      toast.error('Indiquez le nom de l’opération.');
      return;
    }
    if (!(cad > 0)) {
      toast.error('Indiquez une cadence valide (m²/h > 0).');
      return;
    }
    setSavingOpKey(`${catId}:new`);
    try {
      const { data } = await axios.post(
        `${apiBase}/operation-categories/${catId}/operations`,
        { label, cadence_m2h: cad },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      mergeCategory(data);
      setNewOpByCat((prev) => ({ ...prev, [catId]: { label: '', cadence: '' } }));
      toast.success('Opération ajoutée.');
    } catch (e) {
      handleApiError(e);
    } finally {
      setSavingOpKey(null);
    }
  };

  const confirmDeleteOperation = async () => {
    if (!deleteOpTarget) return;
    const { catId, opId } = deleteOpTarget;
    try {
      await axios.delete(`${apiBase}/operation-categories/${catId}/operations/${opId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { data } = await axios.get(`${apiBase}/operation-categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(normalizeCategories(data));
      toast.success('Opération supprimée.');
    } catch (e) {
      handleApiError(e);
    } finally {
      setDeleteOpTarget(null);
    }
  };

  const setDraft = (catId, partial) => {
    setNewOpByCat((prev) => ({
      ...prev,
      [catId]: { label: '', cadence: '', ...prev[catId], ...partial },
    }));
  };

  if (!isActive) return null;

  const inputStyle = {
    borderColor: theme.border,
    backgroundColor: theme.inputBackground,
    color: theme.text,
  };

  return (
    <div className="space-y-4">
      {!canConfigure && (
        <p className="text-sm" style={{ color: theme.textSecondary }}>
          Lecture seule. La modification requiert la permission{' '}
          <span className="font-medium" style={{ color: theme.text }}>
            devis.configure
          </span>
          .
        </p>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <p className="text-sm min-w-0" style={{ color: theme.textSecondary }}>
          Opérations du catalogue chiffrage : nom, cadence en m²/heure, regroupement par catégorie de
          prestation. Les modifications sont enregistrées automatiquement.
        </p>
        {canConfigure && (
          <Button
            type="button"
            size="lg"
            className="gap-2 shrink-0 font-semibold shadow-md transition-[filter,transform] hover:brightness-110 active:scale-[0.99] w-full sm:w-auto sm:min-w-[220px]"
            style={{ backgroundColor: theme.accent, color: '#fff' }}
            disabled={addingCat}
            onClick={addCategory}
          >
            <Plus className="h-5 w-5" strokeWidth={2.25} aria-hidden />
            {addingCat ? 'Création…' : 'Ajouter une catégorie'}
          </Button>
        )}
      </div>

      {loading && (
        <p className="text-sm" style={{ color: theme.textSecondary }}>
          Chargement…
        </p>
      )}

      {!loading && categories.length === 0 && (
        <p className="text-sm" style={{ color: theme.textSecondary }}>
          Aucune catégorie. {canConfigure ? 'Ajoutez une catégorie puis des opérations.' : ''}
        </p>
      )}

      <div className="flex flex-col gap-4">
        {categories.map((cat) => (
          <Card
            key={cat.id}
            className="min-w-0 overflow-hidden rounded-xl border shadow-sm"
            style={{ borderColor: theme.border, backgroundColor: theme.surface }}
          >
            <CardHeader className="space-y-3 border-b p-3 sm:p-3.5" style={{ borderColor: theme.border }}>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div className="min-w-0 flex-1 space-y-1">
                  <Label className="text-xs" style={{ color: theme.textSecondary }}>
                    Catégorie de prestation
                  </Label>
                  <Input
                    value={cat.label}
                    onChange={(e) => updateCategoryField(cat.id, 'label', e.target.value)}
                    disabled={!canConfigure}
                    className="h-8 max-w-xl text-sm"
                    style={inputStyle}
                  />
                </div>
                {canConfigure && (
                  <div className="flex flex-wrap justify-end gap-1.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      aria-label="Supprimer la catégorie"
                      onClick={() => setDeleteCatTarget(cat)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3 p-3 sm:p-3.5">
              <p className="text-xs font-semibold" style={{ color: theme.text }}>
                Opérations
              </p>
              <div className="min-w-0 rounded-lg border" style={{ borderColor: theme.border }}>
                <Table className="table-fixed" wrapperClassName="overflow-visible min-w-0">
                  <colgroup>
                    <col style={{ width: '55%' }} />
                    <col style={{ width: '30%' }} />
                    <col style={{ width: '15%' }} />
                  </colgroup>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent" style={{ borderColor: theme.border }}>
                      <TableHead className="h-8 px-2 text-xs font-medium" style={{ color: theme.text }}>
                        Nom de l’opération
                      </TableHead>
                      <TableHead
                        className="h-8 px-2 text-right text-xs font-medium"
                        style={{ color: theme.text }}
                      >
                        Cadence (m²/h)
                      </TableHead>
                      <TableHead className="h-8 p-0 text-center text-xs font-medium" style={{ color: theme.text }}>
                        {/* actions */}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cat.operations.map((op) => (
                      <TableRow key={op.id} style={{ borderColor: theme.border }}>
                        <TableCell className="p-1.5 align-middle">
                          <Input
                            value={op.label}
                            onChange={(e) => updateOperationField(cat.id, op.id, 'label', e.target.value)}
                            disabled={!canConfigure}
                            className="h-7 w-full min-w-0 text-xs"
                            style={inputStyle}
                          />
                        </TableCell>
                        <TableCell className="p-1.5 align-middle">
                          <Input
                            type="number"
                            min={0}
                            step={0.01}
                            inputMode="decimal"
                            className="h-7 w-full min-w-0 px-2 text-right text-xs tabular-nums"
                            value={op.cadence_m2h === '' ? '' : op.cadence_m2h}
                            onChange={(e) =>
                              updateOperationField(cat.id, op.id, 'cadence_m2h', e.target.value)
                            }
                            disabled={!canConfigure}
                            style={inputStyle}
                          />
                        </TableCell>
                        <TableCell className="p-1 align-middle">
                          {canConfigure ? (
                            <div className="flex items-center justify-center">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-destructive hover:text-destructive"
                                aria-label="Supprimer l’opération"
                                onClick={() => setDeleteOpTarget({ catId: cat.id, opId: op.id })}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                    {canConfigure && (
                      <TableRow style={{ borderColor: theme.border }}>
                        <TableCell className="p-1.5 align-middle">
                          <Input
                            placeholder="Nouvelle opération…"
                            value={newOpByCat[cat.id]?.label ?? ''}
                            onChange={(e) => setDraft(cat.id, { label: e.target.value })}
                            className="h-7 w-full min-w-0 text-xs"
                            style={inputStyle}
                          />
                        </TableCell>
                        <TableCell className="p-1.5 align-middle">
                          <Input
                            type="number"
                            min={0}
                            step={0.01}
                            inputMode="decimal"
                            placeholder="m²/h"
                            className="h-7 w-full min-w-0 px-2 text-right text-xs tabular-nums"
                            value={newOpByCat[cat.id]?.cadence ?? ''}
                            onChange={(e) => setDraft(cat.id, { cadence: e.target.value })}
                            style={inputStyle}
                          />
                        </TableCell>
                        <TableCell className="p-1 align-middle text-center">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            disabled={savingOpKey === `${cat.id}:new`}
                            onClick={() => addOperation(cat.id)}
                          >
                            <Plus className="mr-1 h-3.5 w-3.5" />
                            Ajouter
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={!!deleteCatTarget} onOpenChange={(open) => !open && setDeleteCatTarget(null)}>
        <AlertDialogContent style={{ borderColor: theme.border, backgroundColor: theme.surface }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: theme.text }}>Supprimer cette catégorie ?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: theme.textSecondary }}>
              La catégorie « {deleteCatTarget?.label || '—'} » et toutes ses opérations seront supprimées du
              chiffrage.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDeleteCategory}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteOpTarget} onOpenChange={(open) => !open && setDeleteOpTarget(null)}>
        <AlertDialogContent style={{ borderColor: theme.border, backgroundColor: theme.surface }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: theme.text }}>Supprimer cette opération ?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: theme.textSecondary }}>
              Cette opération sera retirée du catalogue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDeleteOperation}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DevisOperationsTab;
